import { formatISO9075, parse } from 'date-fns';
import {
  AvailableCredits,
  AvailableProduct,
  CreditDto,
  Platform,
  PRODUCT_SORTING_ORDER,
} from '@mkp/shared/models';

type ProductCode = CreditDto['product']['type']; // so we don't import with circular dependencies

// Performance optimizations - extract constants
const slotPrefix = 'SLOT';
const platformOrder = [Platform.Jobs, Platform.JobUp, Platform.Js24, null];
const platformOrderMap = new Map(platformOrder.map((platform, index) => [platform, index]));
const dateFormat = 'dd.MM.y';

const sortProductsByType = (a: AvailableProduct, b: AvailableProduct): number => {
  const aOrder = PRODUCT_SORTING_ORDER[a.type as keyof typeof PRODUCT_SORTING_ORDER] ?? 999;
  const bOrder = PRODUCT_SORTING_ORDER[b.type as keyof typeof PRODUCT_SORTING_ORDER] ?? 999;
  return aOrder - bOrder;
};

// TODO TO MOVE AFTER EPIC MERGE
export const stripSlotPrefix = (productCode: ProductCode | `SLOT${ProductCode}`): ProductCode =>
  productCode.replace(slotPrefix, '') as ProductCode;

export const isSlotType = (
  productCode: ProductCode | `SLOT${ProductCode}`
): productCode is `SLOT${ProductCode}` => productCode.startsWith(slotPrefix);

export const groupCreditQuantityPerPlatform = (credits: CreditDto[]): AvailableCredits[] => {
  const platformCredits = platformOrder
    .filter((platform): platform is Platform => platform !== null) // Exclude null, handle it separately for PPP
    .map((platform) => {
      const aggregate = credits
        .filter((credit) => stripSlotPrefix(credit.product.type).startsWith(platform))
        .reduce((acc, { product, _availableCredits, quantity }) => {
          const existing = acc.find((item) => item.type === product.type);
          if (existing) {
            existing._availableCredits += _availableCredits;
            existing._totalCredits += quantity ?? 0;
          } else {
            acc.push({
              type: product.type,
              _availableCredits,
              _totalCredits: quantity ?? 0,
            });
          }
          return acc;
        }, [] as AvailableProduct[])
        .sort(sortProductsByType); // Sort products by defined order (most expensive first)

      return aggregate.length ? { platform, aggregate } : null;
    })
    .filter(Boolean) as AvailableCredits[];

  const pppCredits = credits.filter((credit) => credit.product.type === 'PAYPERPERFORMANCE');
  if (pppCredits.length > 0) {
    const totalPppQuantity = pppCredits.reduce((sum, credit) => sum + (credit.quantity ?? 0), 0);
    platformCredits.push({
      platform: null,
      aggregate: [
        {
          type: 'PAYPERPERFORMANCE' as ProductCode,
          _availableCredits: 0,
          _totalCredits: totalPppQuantity,
        },
      ],
    });
  }

  return platformCredits;
};

export const isCreditRefundable = (credit: CreditDto): boolean =>
  Array.isArray(credit?._links?.update?.attributes.properties) &&
  credit?._links?.update?.attributes.properties.includes('state');

export const getCurrentAvailableCreditsQuery = (limit = 0, additionalFilter = ``) => ({
  filter:
    `validTo>${formatISO9075(new Date())};validFrom<=${formatISO9075(new Date())}` +
    additionalFilter,
  limit,
});

export const sortPlatforms = (credits: AvailableCredits[]) =>
  credits
    ? credits.sort((a, b) => {
        const aOrder = platformOrderMap.get(a.platform) ?? platformOrder.length;
        const bOrder = platformOrderMap.get(b.platform) ?? platformOrder.length;
        return aOrder - bOrder;
      })
    : [];

export const generateDateWithMonthOffset = (
  date: string | Date,
  monthsToAdd: number,
  overwriteTime?: 'start' | 'end'
): Date => {
  const parsedDate = typeof date === 'string' ? parse(date, dateFormat, new Date()) : date;
  const utcDate = new Date(
    Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate())
  );
  const resultDate = new Date(
    Date.UTC(utcDate.getUTCFullYear(), utcDate.getUTCMonth() + monthsToAdd, utcDate.getUTCDate())
  );

  if (overwriteTime === 'end' && monthsToAdd > 0) {
    resultDate.setUTCDate(resultDate.getUTCDate() - 1);
  }

  const [hours, minutes, seconds, milliseconds] =
    overwriteTime === 'start'
      ? [0, 0, 0, 0]
      : overwriteTime === 'end'
        ? [23, 59, 59, 999]
        : [
            resultDate.getUTCHours(),
            resultDate.getUTCMinutes(),
            resultDate.getUTCSeconds(),
            resultDate.getUTCMilliseconds(),
          ];

  return new Date(
    Date.UTC(
      resultDate.getUTCFullYear(),
      resultDate.getUTCMonth(),
      resultDate.getUTCDate(),
      hours,
      minutes,
      seconds,
      milliseconds
    )
  );
};

export const filterAvailableCredits = (credits: CreditDto[]): CreditDto[] => {
  return credits.filter((credit: CreditDto) => credit._availableCredits > 0);
};

export const groupCreditsByWalletId = (credits: CreditDto[]): Record<string, CreditDto[]> => {
  return credits.reduce(
    (acc, credit) => {
      if (credit.walletId) {
        (acc[credit.walletId] ??= []).push(credit);
      }
      return acc;
    },
    {} as Record<string, CreditDto[]>
  );
};

export const groupAndFilterNonSlotCredits = (credits: CreditDto[]): AvailableCredits[] => {
  const nonSlotCredits = credits.filter((credit) => !isSlotType(credit.product.type));
  return groupCreditQuantityPerPlatform(nonSlotCredits);
};
